import React from "react";
import { Link } from "react-router-dom";
import { FaCalendarCheck, FaBullhorn, FaBook, FaRupeeSign, FaUser } from "react-icons/fa";
import StatCard from "../../../components/StatCard/StatCard";
import Loader from "../../../components/Loader/Loader";
import { useStudentSession } from "../context/StudentSessionContext";
import { useStudentDashboard } from "./hooks/useStudentDashboard";
// Reusing the Fees feature's own hook (rather than duplicating its mock data
// here) so the Dashboard tile and the Fees page always show the same status
// from one source — once that hook is wired to a real endpoint, both update
// together with no change needed here.
import { useStudentFees } from "../Fees/hooks/useStudentFees";
import EventsList from "./components/EventsList";
import WordOfTheDayCard from "./components/WordOfTheDayCard";
import "./Dashboard.css";

const FEES_STATUS_TONE = {
  paid: "success",
  due: "warning",
  overdue: "danger",
};

// Landing page of the Student Portal: the logged-in student's own profile
// header, a "Frequently Used" row of summary tiles, their upcoming events,
// and today's Word of the Day. Renders only its own content — the layout
// shell (sidebar / header / session provider) comes from StudentPortalLayout.
const StudentDashboard = () => {
  const { student } = useStudentSession();
  const { data, isLoading } = useStudentDashboard(
    student?.id,
    student?.student_program?.program_id,
    student?.student_program?.session_id
  );
  const { fees, isLoading: isFeesLoading } = useStudentFees();

  if (isLoading) return <Loader />;

  const { attendancePercent, homeworkCount, circularCount, upcomingEvents, wordOfTheDay } = data;
  // Confirmed against the real backend (2026-10-22): profile_image is a bare
  // relative storage path with no known base URL, but s3_image_path is a
  // ready-to-use, pre-signed S3 URL for the same photo.
  const photoUrl = student?.s3_image_path || null;

  return (
    <div className="student-dashboard">
      <div className="sp-card student-dashboard__profile">
        <div className="student-dashboard__avatar">
          {photoUrl ? <img src={photoUrl} alt="" /> : <FaUser size={28} />}
        </div>
        <div>
          <h2 className="student-dashboard__name">
            {student?.full_name ||
              [student?.first_name, student?.last_name].filter(Boolean).join(" ") ||
              "Student"}
          </h2>
          <p className="student-dashboard__meta">
            Class - {student?.student_program?.program?.name || "—"}
          </p>
          <p className="student-dashboard__meta">Admission Number - {student?.id_number || "—"}</p>
        </div>
      </div>

      <h3 className="sp-section-title student-dashboard__section-title">Frequently Used</h3>
      <div className="row">
        <Link
          to="/student-portal/attendance"
          className="col-xl-3 col-md-6 mb-4 d-block text-decoration-none student-dashboard__stat-link"
        >
          <StatCard
            value={attendancePercent !== null && attendancePercent !== undefined ? `${attendancePercent}%` : "—"}
            label="Attendance"
            tone="warning"
            icon={FaCalendarCheck}
            colClassName=" "
          />
        </Link>
        <Link
          to="/student-portal/circulars"
          className="col-xl-3 col-md-6 mb-4 d-block text-decoration-none student-dashboard__stat-link"
        >
          <StatCard value={circularCount} label="Circulars" tone="danger" icon={FaBullhorn} colClassName=" " />
        </Link>
        <Link
          to="/student-portal/homework"
          className="col-xl-3 col-md-6 mb-4 d-block text-decoration-none student-dashboard__stat-link"
        >
          <StatCard value={homeworkCount} label="Homework" tone="info" icon={FaBook} colClassName=" " />
        </Link>
        <Link
          to="/student-portal/fees"
          className="col-xl-3 col-md-6 mb-4 d-block text-decoration-none student-dashboard__stat-link"
        >
          <StatCard
            value={isFeesLoading ? "—" : fees?.totalDue ? `₹${fees.totalDue.toLocaleString("en-IN")}` : "₹0"}
            label={String(fees?.status || "").toLowerCase() === "paid" ? "Fee Paid" : "Fee Due"}
            tone={FEES_STATUS_TONE[String(fees?.status || "").toLowerCase()] || "brand"}
            icon={FaRupeeSign}
            colClassName=" "
          />
        </Link>
      </div>

      <div className="student-dashboard__content">
        <div>
          <Link
            to="/student-portal/events"
            className="student-dashboard__section-link text-decoration-none"
          >
            <h3 className="sp-section-title student-dashboard__section-title">Upcoming Events</h3>
          </Link>
          <EventsList events={upcomingEvents} />
        </div>
        <div>
          <Link
            to="/student-portal/word-of-the-day"
            className="student-dashboard__section-link text-decoration-none"
          >
            <h3 className="sp-section-title student-dashboard__section-title">Word of the Day</h3>
          </Link>
          <WordOfTheDayCard wordOfTheDay={wordOfTheDay} />
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
